import { useCallback } from "react";

import { generateRecurrenceRuleString } from "../../../../Recurrence";
import {
  combineRunProps,
  parseErrorJson,
  parseErrorResponse,
  useRequestWithFeedback,
  useIdempotencyErrorHandling,
} from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { useSelectedRecipient } from "../../entities/recipients";
import { useInstitution } from "../../entities";
import { useACHCompany } from "../../entities/achCompanies";
import { useLibrary } from "../../../providers";

import {
  formatValuesForRecurrenceString,
  getIsOneTimePaymentForToday,
  getScreenWithError,
} from "./utils";
import { API } from "./api";
import { useIsSingleExpediteEnabled } from "./hooks";
import {
  ACHPaymentForm,
  transformToApiFields,
  transformToFedNowApiFields,
  transformToScheduledApiFields,
} from "./ACHPayment.form";

import type {
  AnyException,
  Data,
  ErrorResponse,
  LayeredOverrideRunProps,
} from "../../../composites";

export const useCreateACHPayment = () => {
  const { send, loading } = useRequestWithFeedback<
    API.CreateACHPayment.Response | API.CreateScheduledACHPayment.Response,
    API.CreateACHPayment.Error | API.CreateScheduledACHPayment.Error
  >();
  const t = useLibrary("translations");
  const { values } = ACHPaymentForm.useForm();
  const recipient = useSelectedRecipient() as API.Recipient;
  const achCompany = useACHCompany() as API.ACHCompany;
  const institution = useInstitution();
  const isSingleExpediteEnabled = useIsSingleExpediteEnabled();
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "payment-idempotency-error",
  );

  const createACHPayment = useCallback(
    (
      idempotencyKey: UUID,
      overrideRunProps: {
        onSuccess?: (data: API.ACHPayment | API.ScheduledACHPayment) => void;
        onError?: (screenWithError: {
          screen: number;
          field?: string;
          message: string;
        }) => void;
        deviceIds?: { deviceId: string; deviceRequestId: string };
        invoice?: string;
      } = {},
    ) => {
      const isOneTimePaymentForToday = getIsOneTimePaymentForToday(
        values,
        institution?.ach_next_cutoff,
      );

      const getRecurrenceRuleString = () => {
        const { frequency, startDate, untilDate, count } =
          formatValuesForRecurrenceString(values);
        const recurringRuleString = generateRecurrenceRuleString(
          frequency,
          startDate,
          untilDate,
          count,
        );
        return recurringRuleString;
      };

      // Use device IDs from overrideRunProps if provided, otherwise use form values
      const deviceId = overrideRunProps.deviceIds?.deviceId ?? values.deviceId;
      const deviceRequestId =
        overrideRunProps.deviceIds?.deviceRequestId ?? values.deviceRequestId;

      const todayOneTimePaymentValues = {
        ...values,
        deviceId,
        deviceRequestId,
        recipient: recipient.id,
        achCompany: achCompany.id,
        invoice: overrideRunProps.invoice,
        isSingleExpediteEnabled,
      };

      const scheduledValues = {
        ...todayOneTimePaymentValues,
        recurringRuleString: getRecurrenceRuleString(),
        transactionType: values.transactionType,
      };

      const transformedValues = isOneTimePaymentForToday
        ? transformToApiFields(todayOneTimePaymentValues)
        : transformToScheduledApiFields(scheduledValues);

      send({
        action: API.createACHPayment(idempotencyKey, transformedValues),
        onSuccess: overrideRunProps.onSuccess,
        onError: async (error) => {
          const json = await error?.response?.json().catch(() => undefined);

          const { errors } = parseErrorJson(json);
          if (canHandleIdempotencyErrors(errors)) return;

          const defaultError = t.getString(
            "ach-payment-error-banner",
            null,
            "We could not process your ACH payment. Please try again.",
          );

          const screenWithError = json
            ? getScreenWithError(json, defaultError)
            : { screen: 1, message: defaultError };

          overrideRunProps.onError?.(screenWithError);
        },
      });
    },
    [
      t,
      values,
      recipient,
      achCompany,
      institution?.ach_next_cutoff,
      isSingleExpediteEnabled,
      send,
      canHandleIdempotencyErrors,
    ],
  );

  return useObjectMemo({
    createACHPayment,
    loading,
  });
};

export const useCreateFedNowPayment = () => {
  const { send, loading } = useRequestWithFeedback<
    API.CreateFedNowPayment.Response,
    API.CreateFedNowPayment.Error
  >();
  const t = useLibrary("translations");
  const { values } = ACHPaymentForm.useForm();
  const recipient = useSelectedRecipient() as API.Recipient;
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "payment-idempotency-error",
  );

  const createFedNowPayment = useCallback(
    (
      idempotencyKey: UUID,
      overrideRunProps: {
        onSuccess?: (data: API.FedNowTransaction) => void;
        onError?: (error: { message: string }) => void;
        deviceIds?: { deviceId: string; deviceRequestId: string };
        invoice?: string;
      } = {},
    ) => {
      const transformedValues = transformToFedNowApiFields({
        ...values,
        deviceId: overrideRunProps.deviceIds?.deviceId ?? values.deviceId,
        deviceRequestId:
          overrideRunProps.deviceIds?.deviceRequestId ?? values.deviceRequestId,
        recipient: recipient.id,
        invoice: overrideRunProps.invoice,
      });

      send({
        action: API.createFedNowPayment(idempotencyKey, transformedValues),
        onSuccess: overrideRunProps.onSuccess,
        onError: async (error) => {
          const json = await error?.response?.json().catch(() => undefined);

          const { errors } = parseErrorJson(json);
          if (canHandleIdempotencyErrors(errors)) return;

          overrideRunProps.onError?.({
            message:
              errors[0]?.description ||
              t.getString(
                "instant-payment-error-banner",
                null,
                "We could not process your instant payment. Please try again.",
              ),
          });
        },
      });
    },
    [t, values, recipient, send, canHandleIdempotencyErrors],
  );

  return useObjectMemo({
    createFedNowPayment,
    loading,
  });
};

export const useDeleteScheduledACHPayment = () => {
  const { send, loading } = useRequestWithFeedback();
  const l10n = useLibrary("translations");
  const { throwToast } = useLibrary("toasts");

  const deleteScheduledACHPayment = useCallback(
    (
      uuid: API.ScheduledACHPaymentId,
      overrideRunProps: LayeredOverrideRunProps<Data, AnyException<Data>> = {},
    ) => {
      return send({
        action: API.deleteScheduledACHPayment(uuid),
        ...combineRunProps(
          {
            onSuccess: async () => {
              throwToast({
                kind: "success",
                message: l10n.getString(
                  "delete-payment-success-message",
                  null,
                  "Payment canceled.",
                ),
              });
            },
            onError: async (error: ErrorResponse<AnyException<Data>>) => {
              const { errors } = await parseErrorResponse(error);
              const defaultError = l10n.getString(
                "delete-payment-error-message",
                null,
                "We could not cancel your payment. Please try again.",
              );
              throwToast({
                kind: "error",
                message: errors.length
                  ? `${errors[0].description}`
                  : defaultError,
              });
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, l10n, throwToast],
  );

  return useObjectMemo({
    deleteScheduledACHPayment,
    loading,
  });
};
